import { InfoCircleOutlined } from '@ant-design/icons';
import { Modal, Tag } from 'antd';
import caregiversNoAi from 'assets/caregivers-no-ai.png';
import laurine from 'assets/laurine.jpg';
import MessageInput from 'components/inputs/MessageInput';
import PaywallModal from 'components/modals/PaywallModal';
import useIsKeyboardVisible from 'hooks/useIsKeyboardVisible';
import usePreferences from 'hooks/usePreferences';
import posthog from 'posthog-js';
import Keys from 'preferences/preferences';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Trans, useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { useSelector } from 'react-redux';
import { selectDoesUserHaveFullAccess } from 'store/users/selectors';
import ScrollUtil from 'utils/ScrollUtil';
import { Button } from 'components/ui';
import { colors, dropShadow, shadows } from 'design';

const useStyles = createUseStyles({
  container: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: colors.surface.page,
    paddingBottom: 64,
  },

  // Hero section
  heroSection: {
    padding: '24px 16px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  heroTitle: {
    fontSize: '1.35rem',
    paddingRight: 32,
    marginTop: 24,
    fontWeight: 'bold',
    lineHeight: 1.2,
    margin: 0,
    color: colors.text.primary,
  },
  heroTitleItalic: {
    fontStyle: 'italic',
    color: colors.brand.accentStrong,
  },
  heroSubtitle: {
    fontSize: '0.95rem',
    color: colors.text.tertiary,
    margin: 0,
    lineHeight: 1.5,
  },

  // Caregiver card
  caregiverCard: {
    backgroundColor: 'white',
    borderRadius: 16,
    padding: 16,
    marginTop: 32,
    marginBottom: 8,
    boxShadow: shadows.sm,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  caregiverCardHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  caregiverCardProfile: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  caregiverAvatar: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    backgroundColor: colors.brand.accentSubtle,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.2rem',
    position: 'relative',
  },
  caregiverOnlineDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    backgroundColor: colors.feedback.success.solid,
    position: 'absolute',
    bottom: 1,
    right: 1,
    border: '2px solid white',
  },
  caregiverName: {
    fontWeight: 'bold',
    margin: 0,
    fontSize: '0.95rem',
    color: colors.text.primary,
  },
  caregiverRole: {
    margin: 0,
    fontSize: '0.8rem',
    color: colors.text.tertiary,
  },
  chatIconContainer: {
    color: colors.brand.accentStrong,
    fontSize: '1.4rem',
  },
  patientAndCaregiverBubbles: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
  },
  patientBubble: {
    alignSelf: 'flex-end',
    backgroundColor: 'rgb(217,246,218)',
    background: 'linear-gradient(130deg, rgba(219, 255, 239,1) 0%, rgba(191,233,216,1) 100%)',
    color: 'rgba(46, 46, 46, 0.88)',
    borderRadius: '16px 16px 4px 16px',
    padding: '8px 12px',
    maxWidth: '80%',
    fontSize: '0.9rem',
    margin: 0,
  },
  caregiverBubble: {
    alignSelf: 'flex-start',
    backgroundColor: colors.surface.muted,
    color: colors.text.primary,
    borderRadius: '16px 16px 16px 4px',
    padding: '8px 12px',
    maxWidth: '80%',
    fontSize: '0.9rem',
  },
  cardTrust: {
    textAlign: 'center',
    fontSize: '0.78rem',
    color: colors.text.disabled,
    margin: 0,
  },

  // How it works section
  howItWorksSection: {
    padding: '32px 16px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  howItWorksLabel: {
    fontSize: '0.7rem',
    fontWeight: 'bold',
    letterSpacing: '0.1em',
    color: colors.text.tertiary,
    textTransform: 'uppercase',
    margin: 0,
  },
  howItWorksTitle: {
    fontSize: '1.35rem',
    fontWeight: 'bold',
    lineHeight: 1.2,
    margin: '8px 0 16px',
    color: colors.text.primary,
  },
  howItWorksTitleItalic: {
    fontStyle: 'italic',
    color: colors.brand.accentStrong,
  },
  stepsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    marginTop: 8,
  },
  step: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 16,
  },
  stepNumber: {
    minWidth: 36,
    height: 36,
    borderRadius: '50%',
    backgroundColor: colors.brand.accentSubtle,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    fontSize: '0.9rem',
    color: colors.brand.accentStrong,
    flexShrink: 0,
  },
  stepContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    paddingTop: 4,
  },
  stepTitle: {
    fontWeight: 'bold',
    margin: 0,
    fontSize: '1rem',
    color: colors.text.primary,
  },
  stepDescription: {
    margin: 0,
    fontSize: '0.88rem',
    color: colors.text.tertiary,
    lineHeight: 1.5,
  },
  stepConnector: {
    width: 2,
    height: 24,
    backgroundColor: colors.brand.accentSubtle,
    marginLeft: 17,
    marginTop: -8,
    marginBottom: -8,
  },

  // Team section
  teamSection: {
    margin: '32px 16px 32px',
    backgroundColor: colors.brand.accentSubtle,
    borderRadius: 16,
    padding: '20px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  teamLabel: {
    fontSize: '0.7rem',
    fontWeight: 'bold',
    letterSpacing: '0.1em',
    color: colors.brand.accentStrong,
    textTransform: 'uppercase',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  teamTitle: {
    fontSize: '1.2rem',
    fontWeight: 'bold',
    lineHeight: 1.3,
    margin: '4px 0',
    color: colors.text.primary,
  },
  teamSubtitle: {
    fontSize: '0.88rem',
    color: colors.text.tertiary,
    margin: '0 0 4px',
    lineHeight: 1.5,
  },
  specialtyGroupLabel: {
    fontSize: '0.68rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: colors.brand.accentStrong,
    margin: '4px 0 4px',
  },
  specialtyTagsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  },
  specialtyTag: {
    borderRadius: 20,
    padding: '4px 12px',
    fontSize: '0.85rem',
    backgroundColor: 'rgba(255,255,255,0.65)',
    border: '1px solid rgba(255,255,255,0.9)',
    color: colors.text.primary,
    cursor: 'default',
  },

  // Quote section
  quoteSection: {
    margin: '32px 16px 16px',
    backgroundColor: colors.brand.accentSubtle,
    borderRadius: 16,
    padding: '20px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  quoteText: {
    fontSize: '1.1rem',
    fontStyle: 'italic',
    lineHeight: 1.4,
    margin: 0,
    color: colors.text.primary,
  },
  quoteItalic: {
    fontStyle: 'italic',
    fontWeight: 'bold',
  },
  quoteDescription: {
    fontSize: '0.85rem',
    color: colors.text.tertiary,
    margin: 0,
    lineHeight: 1.5,
  },

  // CTA section
  ctaSection: {
    padding: '8px 16px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  ctaTitle: {
    textAlign: 'center',
    margin: 0,
    fontSize: '1.3rem',
    fontWeight: 'bold',
    color: colors.text.primary,
  },
  messageInputContainer: {
    width: '100%',
  },

  // Floating CTA — portalled into pageContainer (position: relative) so it
  // sits above the scroll content but below the nav bar
  '@keyframes floatIn': {
    from: { opacity: 0, transform: 'translateY(10px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
  floatingCta: {
    position: 'absolute',
    bottom: 16,
    left: 16,
    right: 16,
    zIndex: 100,
    filter: dropShadow('md'),
    animation: '$floatIn 0.2s ease',
  },

  // Emergency
  inCaseOfEmergency: {
    textAlign: 'center',
    color: colors.text.tertiary,
    fontSize: '0.8rem',
    padding: '8px 16px 24px',
    margin: 0,
    '& svg': {
      marginRight: 6,
    },
  },
});

export type ChatExplainerProps = {
  scrollableElement: React.RefObject<HTMLDivElement>;
  disableNewConversationButton?: boolean;
};

const SPECIALTY_TAGS_MEDICAL = [
  { emoji: '🌸', labelKey: 'chats.explainerV2.specialties.gynecologists' },
  { emoji: '🌿', labelKey: 'chats.explainerV2.specialties.midwives' },
  { emoji: '🤲', labelKey: 'chats.explainerV2.specialties.physiotherapists' },
  { emoji: '🥑', labelKey: 'chats.explainerV2.specialties.nutritionists' },
  { emoji: '💬', labelKey: 'chats.explainerV2.specialties.psychologists' },
  { emoji: '👶', labelKey: 'chats.explainerV2.specialties.childcareNurses' },
  { emoji: '🦴', labelKey: 'chats.explainerV2.specialties.osteopaths' },
  { emoji: '👩‍⚕️', labelKey: 'chats.explainerV2.specialties.nurses' },
];

const SPECIALTY_TAGS_SOFT = [
  { emoji: '🌱', labelKey: 'chats.explainerV2.specialties.naturopaths' },
  { emoji: '🧘', labelKey: 'chats.explainerV2.specialties.sophrologists' },
];

export default function ChatExplainer({ scrollableElement, disableNewConversationButton = false }: ChatExplainerProps) {
  const { t } = useTranslation();
  const styles = useStyles();
  const { isKeyboardVisible } = useIsKeyboardVisible();
  const [isPaywallOpen, setIsPaywallOpen] = useState(false);
  const doesUserHaveFullAccess = useSelector(selectDoesUserHaveFullAccess);

  const { value: didEnableFreeMessagesValue, set: setDidEnableFreeMessages } = usePreferences(
    Keys.PAYWALL_DID_ENABLE_FREE_MESSAGES,
  );
  const didEnableFreeMessages = didEnableFreeMessagesValue === true;

  const [isStarted, setIsStarted] = useState(false);
  const [isInspirationModalOpen, setIsInspirationModalOpen] = useState(false);

  const exampleMessages = t<string, { returnObjects: true }, string[]>('chats.new.exampleMessages', {
    returnObjects: true,
  });

  const handleStart = () => {
    if (doesUserHaveFullAccess || didEnableFreeMessages) {
      setIsStarted(true);
      posthog.capture('chats.start_conversation_clicked', { source: 'explainer_v2' });
    } else {
      setIsPaywallOpen(true);
      posthog.capture('paywall.opened', { source: 'explainer_v2' });
    }
  };

  const ctaRef = useRef<HTMLDivElement>(null);
  const [showFloatingCta, setShowFloatingCta] = useState(false);

  useEffect(() => {
    if (disableNewConversationButton) return;
    const el = ctaRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => setShowFloatingCta(!entry.isIntersecting), {
      root: scrollableElement.current,
      threshold: 1,
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [disableNewConversationButton, scrollableElement]);

  useEffect(() => {
    if (isStarted) {
      scrollableElement.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [isStarted, scrollableElement]);

  useEffect(() => {
    if (isKeyboardVisible) {
      ScrollUtil.scrollToBottom(scrollableElement.current);
    }
  }, [isKeyboardVisible, scrollableElement]);

  return (
    <div className={styles.container}>
      {/* Hero section */}
      <div className={styles.heroSection}>
        <h1 className={styles.heroTitle}>
          <Trans
            i18nKey="chats.explainerV2.heroTitle"
            components={{ italic: <span className={styles.heroTitleItalic} /> }}
          />
        </h1>
        <p className={styles.heroSubtitle}>{t('chats.explainerV2.heroSubtitle')}</p>

        <div className={styles.caregiverCard}>
          <div className={styles.caregiverCardHeader}>
            <div className={styles.caregiverCardProfile}>
              <div className={styles.caregiverAvatar} ref={ctaRef}>
                <img
                  src={laurine}
                  alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                />
                <div className={styles.caregiverOnlineDot} />
              </div>
              <div>
                <p className={styles.caregiverName}>{t('chats.explainerV2.caregiverName')}</p>
                <p className={styles.caregiverRole}>{t('chats.explainerV2.caregiverRole')}</p>
              </div>
            </div>
          </div>

          {isStarted ? (
            <div className={styles.messageInputContainer}>
              <MessageInput animatePlaceholder minRows={2} containerStyle={{ paddingRight: 0 }} compact />
              <Button variant="link" block onClick={() => setIsInspirationModalOpen(true)}>
                {t('chats.explainer.noInspiration')}
              </Button>
            </div>
          ) : (
            <>
              <div className={styles.patientAndCaregiverBubbles}>
                <p className={styles.patientBubble}>{t('chats.explainerV2.patientBubble')}</p>
                <p className={styles.caregiverBubble}>{t('chats.explainerV2.caregiverBubble')}</p>
              </div>

              {!disableNewConversationButton && (
                <Button
                  variant="primary"
                  size="lg"
                  block
                  onClick={handleStart}
                  style={{ boxShadow: `0 4px 16px 0 ${colors.brand.accent}55` }}
                >
                  {t('chats.explainerV2.startConversationCta')}
                </Button>
              )}
            </>
          )}

          <p className={styles.cardTrust}>{t('chats.explainerV2.trustBadges')}</p>
        </div>
      </div>

      {/* Team section */}
      <div className={styles.teamSection}>
        <p className={styles.teamLabel}>
          <img src={caregiversNoAi} alt="" style={{ width: 22, height: 22, borderRadius: 6 }} />
          {t('chats.explainerV2.teamLabel')}
        </p>
        <h3 className={styles.teamTitle}>{t('chats.explainerV2.teamTitle')}</h3>
        <p className={styles.teamSubtitle}>{t('chats.explainerV2.teamSubtitle')}</p>
        <p className={styles.specialtyGroupLabel}>{t('chats.explainerV2.specialties.groupMedical')}</p>
        <div className={styles.specialtyTagsContainer}>
          {SPECIALTY_TAGS_MEDICAL.map(tag => (
            <Tag key={tag.labelKey} className={styles.specialtyTag}>
              {tag.emoji} {t(tag.labelKey)}
            </Tag>
          ))}
        </div>
        <p className={styles.specialtyGroupLabel}>{t('chats.explainerV2.specialties.groupSoft')}</p>
        <div className={styles.specialtyTagsContainer}>
          {SPECIALTY_TAGS_SOFT.map(tag => (
            <Tag key={tag.labelKey} className={styles.specialtyTag}>
              {tag.emoji} {t(tag.labelKey)}
            </Tag>
          ))}
        </div>
      </div>

      {/* How it works section */}
      <div className={styles.howItWorksSection}>
        <p className={styles.howItWorksLabel}>{t('chats.explainerV2.howItWorksLabel')}</p>
        <h2 className={styles.howItWorksTitle}>
          <Trans
            i18nKey="chats.explainerV2.howItWorksTitle"
            components={{ italic: <span className={styles.howItWorksTitleItalic} /> }}
          />
        </h2>

        <div className={styles.stepsList}>
          {/* Step 1 */}
          <div className={styles.step}>
            <div className={styles.stepNumber}>01</div>
            <div className={styles.stepContent}>
              <p className={styles.stepTitle}>{t('chats.explainerV2.step1Title')}</p>
              <p className={styles.stepDescription}>{t('chats.explainerV2.step1Description')}</p>
            </div>
          </div>
          <div className={styles.stepConnector} />

          {/* Step 2 */}
          <div className={styles.step}>
            <div className={styles.stepNumber}>02</div>
            <div className={styles.stepContent}>
              <p className={styles.stepTitle}>{t('chats.explainerV2.step2Title')}</p>
              <p className={styles.stepDescription}>{t('chats.explainerV2.step2Description')}</p>
            </div>
          </div>
          <div className={styles.stepConnector} />

          {/* Step 3 */}
          <div className={styles.step}>
            <div className={styles.stepNumber}>03</div>
            <div className={styles.stepContent}>
              <p className={styles.stepTitle}>{t('chats.explainerV2.step3Title')}</p>
              <p className={styles.stepDescription}>{t('chats.explainerV2.step3Description')}</p>
            </div>
          </div>
          <div className={styles.stepConnector} />

          {/* Step 4 */}
          <div className={styles.step}>
            <div className={styles.stepNumber}>04</div>
            <div className={styles.stepContent}>
              <p className={styles.stepTitle}>{t('chats.explainerV2.step4Title')}</p>
              <p className={styles.stepDescription}>{t('chats.explainerV2.step4Description')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quote section */}
      <div className={styles.quoteSection}>
        <p className={styles.quoteText}>
          <Trans i18nKey="chats.explainerV2.quote" components={{ italic: <span className={styles.quoteItalic} /> }} />
        </p>
        <p className={styles.quoteDescription}>{t('chats.explainerV2.quoteDescription')}</p>
      </div>

      {/* Emergency */}
      <p className={styles.inCaseOfEmergency}>
        <InfoCircleOutlined />
        {t('chats.explainer.inCaseOfEmergency')}
      </p>

      <Modal
        open={isInspirationModalOpen}
        onCancel={() => setIsInspirationModalOpen(false)}
        onOk={() => setIsInspirationModalOpen(false)}
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        <h3>Que dire dans mon premier message ?</h3>
        <Trans
          i18nKey="chats.explainer.firstMessageModal"
          components={{ strong: <strong />, p: <p />, ul: <ul />, li: <li />, quote: <q /> }}
        />
        <ul>{Array.isArray(exampleMessages) && exampleMessages.map(msg => <li key={msg}>{msg}</li>)}</ul>
      </Modal>

      <PaywallModal
        isOpen={isPaywallOpen}
        onClose={() => setIsPaywallOpen(false)}
        closeModal={() => setIsPaywallOpen(false)}
        onFreeMessagesEnabled={() => {
          setDidEnableFreeMessages('true');
          setIsStarted(true);
        }}
      />

      {showFloatingCta &&
        !disableNewConversationButton &&
        scrollableElement.current?.parentElement &&
        createPortal(
          <div className={styles.floatingCta}>
            <Button
              variant="primary"
              size="lg"
              block
              onClick={() => {
                if (isStarted) {
                  scrollableElement.current?.scrollTo({ top: 0, behavior: 'smooth' });
                } else {
                  handleStart();
                }
              }}
            >
              {t('chats.explainerV2.startConversationCta')}
            </Button>
          </div>,
          scrollableElement.current.parentElement,
        )}
    </div>
  );
}
